// @ts-nocheck
import _ from "lodash";

import { take, call, fork, race, select, cancel } from "redux-saga/effects";
import delay from "@redux-saga/delay-p";
import { UAParser } from "ua-parser-js";
import { v4 as uuidv4 } from "uuid";
import ApiClient from "services/ApiClient";
import ApiClientViewTracker from "services/ApiClientViewTracker";
import FullStory from "services/Fullstory";
import moment from "moment";

import Storage from "core/Storage";

import STORAGE from "../constants/STORAGE";

import { appActions } from "../containers/App/App.slice";

import { getClickCount, setClickCount } from "./clickCounter.saga";

// Records sent at seconds: 1, 5, 10, 20, 30, 60
const RECORD_INTERVALS_MS = [ 1000, 4000, 5000, 10000, 10000, 30000 ];
// @TODO: Remove this after testing
const IS_PRODUCTION_ENV = import.meta.env.PROD && import.meta.env.MODE === "production";

const getSessionInfo = (): { sessionId: string, isFirstSession: boolean } => {
  const sessionIdFromStorage = Storage.Session.get(STORAGE.sessionId);
  if (_.isNil(sessionIdFromStorage)) {
    const newSessionId = uuidv4();
    Storage.Session.set(STORAGE.sessionId, newSessionId);
    return {
      sessionId: newSessionId,
      isFirstSession: true
    };
  }
  return {
    sessionId: sessionIdFromStorage,
    isFirstSession: false
  };
};
const deviceInfo = UAParser(window.navigator.userAgent);


function calculateScrollDepthPct() {
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  const windowHeight = window.innerHeight;
  const docHeight = document.documentElement.scrollHeight;

  const totalScrollable = docHeight - windowHeight;
  const scrolled = (scrollTop / totalScrollable) * 100;

  return Math.min(Math.round(scrolled), 100);
}

function getMappingDeviceType(deviceType?: string) {
  const mapping = {
    "mobile": "mobile",
    "tablet": "tablet",
    "desktop": "desktop"
  };
  return mapping[deviceType?.toLowerCase() || ""] || "desktop";
}

function getMappingOs(osName?: string) {
  const os = osName?.toLowerCase() || "";
  if (os.includes("android")) return "android";
  if (os.includes("ios") || os.includes("iphone") || os.includes("ipad")) return "ios";
  if (os.includes("windows")) return "windows";
  if (os.includes("mac")) return "macOS";
  if (os.includes("linux")) return "linux";
  if (os.includes("chrome")) return "chromeOS";
  return "unknown";
}

function getMappingBrowser(browserName?: string) {
  const browser = browserName?.toLowerCase() || "";
  if (browser.includes("chrome") || browser.includes("chromium")) return "chrome";
  if (browser.includes("safari")) return "safari";
  if (browser.includes("samsung")) return "samsungInternet";
  if (browser.includes("firefox")) return "firefox";
  if (browser.includes("edge")) return "edge";
  if (browser.includes("opera")) return "opera";
  if (browser.includes("webview")) return "webView";
  if (browser.includes("facebook") || browser.includes("instagram") || browser.includes("tiktok") || browser.includes("snapchat")) return "socialInApp";
  return "unknown";
}

function getLogObject({ customerId, activeRoute, previousRoute }): PageViewInsertDto {
  const { sessionId, isFirstSession } = getSessionInfo();
  const referrerUrl = (() => {
    if (isFirstSession) {
      if (document.referrer) return document.referrer;
      else if (previousRoute) {
        return `${window.location.origin}${previousRoute?.pathname}`;
      }
      else return undefined;
    }
    return undefined;
  })();

  return {
    customerId,
    sessionId,
    pageUrl: window.location.href,
    pageName: activeRoute?.pathname,
    viewedAt: moment().toISOString(),
    referrerUrl,
    deviceType: getMappingDeviceType(_.get(deviceInfo, "device.type")),
    os: getMappingOs(_.get(deviceInfo, "os.name")),
    browser: getMappingBrowser(_.get(deviceInfo, "browser.name")),
    scrollDepthPct: calculateScrollDepthPct() || 0,
    interactionCount: getClickCount()
  };
}

function* sendLogs({ customerId, activeRoute, previousRoute }) {
  try {
    if (!customerId) return;
    const logObject = getLogObject({
      customerId,
      activeRoute,
      previousRoute
    });
    // @TODO: Remove this after testing
    const _requestHandler = IS_PRODUCTION_ENV ? ApiClientViewTracker.request : ApiClient.request;
    /* const { data, errors } = yield call(ApiClientViewTracker.request, { */
    const { data, errors } = yield call(_requestHandler, {
      method: "post",
      url: "/api/membership/pageViewInsert",
      data: logObject
    });
    if (!data || errors) throw errors;
    FullStory.sendEvent("Page View Insert", {
      logObject
    });
  }
  catch (error) {
    ErrorLoggerService("error", "viewTrackerSaga", error);
  }
}

function* handleLogs() {
  setClickCount(0);
  const activeRoute = yield select((state) => _.get(state, "app.activeRoute"));
  const previousRoute = yield select((state) => _.get(state, "app.previousRoute"));
  const customerId = yield select((state) => _.get(state, "app.memberDetails.id"));

  if (!customerId) return;

  for (const intervalMs of RECORD_INTERVALS_MS) {
    const { pageChanged } = yield race({
      pageChanged: take(appActions.routeEnter),
      stillSamePage: call(delay, intervalMs)
    });

    if (Object.keys(pageChanged || {}).length) {
      return;
    }

    yield fork(sendLogs, {
      customerId,
      activeRoute,
      previousRoute
    });
  }
}

function* initViewTracker() {
  let activeTask = null;

  try {
    // Start tracking immediately on login for the current route
    activeTask = yield fork(handleLogs);

    while (true) {
      yield take(appActions.routeEnter);

      // Cancel any running handleLogs task (and its children)
      if (activeTask) {
        yield cancel(activeTask);
      }

      // Start new handleLogs as a child task
      activeTask = yield fork(handleLogs);
    }
  }
  catch (err) {
    ErrorLoggerService("error", "viewTrackerSaga", err);
  }
}


export default function* sagaWatcher() {
  // @TODO: Remove this after testing
  /* const isProductionEnv = import.meta.env.PROD && import.meta.env.MODE === "production"; */
  const _isProductionEnv = true;
  if (_isProductionEnv) {
    while (true) {
      yield take(appActions.authUserLogin);
      const initViewTrackerTask = yield fork(initViewTracker);
      yield take(appActions.authUserLogout);
      yield cancel(initViewTrackerTask);
    }
  }
}
